/**
 * Overseeding calculator engine — pure functions, no side effects.
 *
 * Answers two questions the "Is It Too Late To Seed?" tool does not: WHEN is my
 * overseeding window (with a live countdown) and HOW MANY pounds of seed do I
 * buy for my grass, lawn size, and how thin the turf is.
 *
 * It composes two existing, tested pieces instead of re-deriving anything:
 *   - Timing comes from `calculateSeedingWindows` (lib/seeding-calculator.ts),
 *     which resolves fall (primary) + spring (secondary) windows from regional
 *     frost dates and already fixes the fall/spring year-shift bug class
 *     (see that file's comment at the year-aware picker). We pass an injectable
 *     `referenceDate` straight through so this stays deterministically testable.
 *   - Quantity comes from `grass-characteristics.seedingRate.overseed*` — the
 *     same canonical rate table the seeding tool's own estimate reads — scaled
 *     by a lawn-condition multiplier and the coated-seed 1.5x factor.
 *
 * St. Augustine has no viable seed, so it returns `mode: 'stolons-only'` with a
 * plug-tray estimate and no seed quantity, mirroring `STOLONS_ONLY` in
 * lib/grass-seed/calculator.ts.
 */

import { GRASS_CHARACTERISTICS } from '@/data/grass-characteristics'
import { getRegionFromZipCode } from '@/data/regional-climate'
import { calculateSeedingWindows } from '@/lib/seeding-calculator'
import { getProductByKey, type ProductKey } from '@/data/guide-product-recommendations'
import { SEED_BY_GRASS, STARTER, normalizeGrass } from '@/lib/product-picks/seed-by-grass'

export type OverseedGrassId =
  | 'fescue'
  | 'kbg'
  | 'rye'
  | 'fine'
  | 'bermuda'
  | 'zoysia'
  | 'centipede'
  | 'staug'
  | 'annualrye'
  | 'coolmix'

export type LawnCondition = 'thin' | 'patchy' | 'mostly-bare'
export type SeedCoating = 'coated' | 'uncoated'

export type OverseedVerdict =
  | 'in-window'
  | 'closing-soon'
  | 'upcoming'
  | 'missed-fall-use-spring'
  | 'plan-next-window'

export type WindowState = 'active' | 'closing' | 'upcoming' | 'passed'

const DAY_MS = 24 * 60 * 60 * 1000

interface GrassMeta {
  /** GRASS_CHARACTERISTICS key + seeding-engine grass name. */
  display: string
  /** Short UI label (chips, cards). */
  label: string
  season: 'cool' | 'warm'
  /** Catalog seed key for the affiliate pick; null when established from plugs. */
  seedKey: string | null
}

// Compact id set (matches the design spec) -> canonical grass data. The display
// name is both the GRASS_CHARACTERISTICS key AND what `calculateSeedingWindows`
// expects, so one string threads through both engines.
export const OVERSEED_GRASSES: Record<OverseedGrassId, GrassMeta> = {
  fescue: { display: 'Tall Fescue', label: 'Tall Fescue', season: 'cool', seedKey: 'seed-nightshift-fescue' },
  kbg: { display: 'Kentucky Bluegrass', label: 'Kentucky Bluegrass', season: 'cool', seedKey: 'seed-nightshift-kbg' },
  rye: { display: 'Perennial Ryegrass', label: 'Perennial Rye', season: 'cool', seedKey: 'seed-nightshift-ryegrass' },
  fine: { display: 'Fine Fescue', label: 'Fine Fescue', season: 'cool', seedKey: 'seed-fescue-jonathan-green' },
  bermuda: { display: 'Bermudagrass', label: 'Bermuda', season: 'warm', seedKey: 'seed-bermuda' },
  zoysia: { display: 'Zoysiagrass', label: 'Zoysia', season: 'warm', seedKey: 'seed-zoysia-zenith' },
  centipede: { display: 'Centipede', label: 'Centipede', season: 'warm', seedKey: 'seed-centipede-pennington' },
  staug: { display: 'St. Augustinegrass', label: 'St. Augustine', season: 'warm', seedKey: null },
  // Annual rye is the classic winter-overseed annual; the catalog's premium
  // overseed pick is the perennial-rye seed (same use case, better turf).
  annualrye: { display: 'Annual Ryegrass', label: 'Annual Rye', season: 'cool', seedKey: 'seed-nightshift-ryegrass' },
  coolmix: { display: 'Mixed Cool-Season Blend', label: 'Cool-Season Mix', season: 'cool', seedKey: 'seed-scotts-sun-shade' },
}

export const OVERSEED_GRASS_OPTIONS: { id: OverseedGrassId; label: string }[] = (
  Object.keys(OVERSEED_GRASSES) as OverseedGrassId[]
).map((id) => ({ id, label: OVERSEED_GRASSES[id].label }))

// Warm-season regions where a winter perennial-ryegrass overseed for green
// dormant turf is a common practice worth surfacing.
const WINTER_RYE_REGIONS = new Set([
  'Southeast',
  'South Central',
  'Florida',
  'Desert Southwest',
  'Hawaii',
])

/** Condition -> where in the [min, max] overseed rate range we land. */
export const CONDITION_RATE: Record<LawnCondition, (min: number, max: number) => number> = {
  thin: (min, max) => (min + max) / 2,
  patchy: (_min, max) => max,
  'mostly-bare': (_min, max) => max * 1.5,
}

export const COATED_MULTIPLIER = 1.5

export interface OverseedWindow {
  key: 'fall' | 'spring'
  isBest: boolean
  startDate: Date
  endDate: Date
  /** Whole days from `referenceDate` to `startDate` (negative once open). */
  daysUntilStart: number
  /** Whole days from `referenceDate` to `endDate` (how long is left). */
  daysLeft: number
  state: WindowState
}

export interface OverseedSeed {
  ratePer1000: number
  lbsRecommended: number
  coated: boolean
  /** Greedy 25/10/5 lb bag combo, e.g. "2×25 lb + 1×10 lb". */
  bagCombo: string
  bagsTotalLbs: number
}

export interface OverseedProduct {
  name: string
  url: string
  imageUrl: string | null
  why: string
}

export interface OverseedingInput {
  zipCode: string
  grassType: OverseedGrassId
  lawnSizeSqFt: number
  seedType?: SeedCoating
  lawnCondition?: LawnCondition
  /** Injectable "now" for deterministic timing. Defaults to new Date(). */
  referenceDate?: Date
}

export interface OverseedingResult {
  region: string
  regionKnown: boolean
  season: 'cool' | 'warm'
  grassId: OverseedGrassId
  grassLabel: string
  grassDisplay: string
  mode: 'seed' | 'stolons-only'
  condition: LawnCondition
  verdict: OverseedVerdict | null
  /** [best] or [best, alt]; empty when the region is unknown. */
  windows: OverseedWindow[]
  /** Nearest actionable window (drives the countdown / verdict callout). */
  focusWindow: OverseedWindow | null
  seed: OverseedSeed | null
  /** St. Augustine only: plug trays for ~20% thin turf. */
  plugTrays: number | null
  product: OverseedProduct | null
  starterProduct: { name: string; url: string } | null
  showWinterRyegrass: boolean
  showBareNudge: boolean
}

function wholeDays(from: Date, to: Date): number {
  return Math.round((to.getTime() - from.getTime()) / DAY_MS)
}

/** Greedy 25/10/5 lb bag combo. Rounds the pounds need up to the nearest 5. */
export function buildBagCombo(lbs: number): { combo: string; totalLbs: number } {
  let need = Math.max(5, Math.ceil(lbs / 5) * 5)
  const totalLbs = need
  const parts: string[] = []
  for (const size of [25, 10, 5]) {
    const n = Math.floor(need / size)
    if (n > 0) {
      parts.push(`${n}×${size} lb`)
      need -= n * size
    }
  }
  return { combo: parts.join(' + '), totalLbs }
}

function classifyWindow(now: Date, start: Date, end: Date): WindowState {
  const daysLeft = wholeDays(now, end)
  if (now >= start && now <= end) return daysLeft <= 21 ? 'closing' : 'active'
  if (now < start) return 'upcoming'
  return 'passed'
}

export function calculateOverseeding(input: OverseedingInput): OverseedingResult {
  const now = input.referenceDate ?? new Date()
  const meta = OVERSEED_GRASSES[input.grassType]
  const condition: LawnCondition = input.lawnCondition ?? 'thin'
  const coated = input.seedType === 'coated'
  const area = Number.isFinite(input.lawnSizeSqFt) ? Math.max(0, input.lawnSizeSqFt) : 0

  const region = getRegionFromZipCode(input.zipCode)
  const regionKnown = region !== 'Unknown'

  const stolonsOnly = meta.seedKey === null

  // ---- Quantity (independent of region; shown even before a ZIP is entered) ----
  let seed: OverseedSeed | null = null
  let plugTrays: number | null = null
  if (stolonsOnly) {
    plugTrays = Math.max(1, Math.ceil((area * 0.2) / 32))
  } else {
    const rate = GRASS_CHARACTERISTICS[meta.display]?.seedingRate
    const min = rate?.overseedLbsPer1000Min ?? 4
    const max = rate?.overseedLbsPer1000Max ?? 6
    let ratePer1000 = CONDITION_RATE[condition](min, max)
    if (coated) ratePer1000 *= COATED_MULTIPLIER
    const lbsRecommended = (ratePer1000 * area) / 1000
    const { combo, totalLbs } = buildBagCombo(lbsRecommended)
    seed = {
      ratePer1000: Math.round(ratePer1000 * 100) / 100,
      lbsRecommended: Math.round(lbsRecommended * 10) / 10,
      coated,
      bagCombo: combo,
      bagsTotalLbs: totalLbs,
    }
  }

  // ---- Timing windows (reuse the frost-based seeding engine) ----
  const windows: OverseedWindow[] = []
  let verdict: OverseedVerdict | null = null
  let focusWindow: OverseedWindow | null = null

  if (regionKnown) {
    try {
      const seeding = calculateSeedingWindows(meta.display, input.zipCode, now)
      const primary = seeding.primaryWindow
      const secondary = seeding.secondaryWindow

      if (primary) {
        // Use optimalStart..acceptableEnd as the realistic actionable range.
        const start = primary.optimalStart
        const end = primary.acceptableEnd
        windows.push({
          key: primary.season === 'fall' ? 'fall' : 'spring',
          isBest: true,
          startDate: start,
          endDate: end,
          daysUntilStart: wholeDays(now, start),
          daysLeft: wholeDays(now, end),
          state: classifyWindow(now, start, end),
        })
      }
      if (secondary) {
        const start = secondary.optimalStart
        const end = secondary.acceptableEnd
        windows.push({
          key: secondary.season === 'fall' ? 'fall' : 'spring',
          isBest: false,
          startDate: start,
          endDate: end,
          daysUntilStart: wholeDays(now, start),
          daysLeft: wholeDays(now, end),
          state: classifyWindow(now, start, end),
        })
      }

      const best = windows.find((w) => w.isBest) ?? null
      const alt = windows.find((w) => !w.isBest) ?? null

      if (best) {
        if (best.state === 'active') {
          verdict = 'in-window'
          focusWindow = best
        } else if (best.state === 'closing') {
          verdict = 'closing-soon'
          focusWindow = best
        } else if (best.state === 'upcoming') {
          // When the alt (spring) window opens sooner than the best (fall) one,
          // spring is the nearest actionable window. Only call it "missed fall"
          // if this year's fall has actually passed (the window rolled to a
          // later year) — in Jan–Apr fall is simply still ahead.
          const fallRolled = best.startDate.getFullYear() > now.getFullYear()
          if (alt && alt.state !== 'passed' && alt.daysUntilStart < best.daysUntilStart) {
            verdict = fallRolled ? 'missed-fall-use-spring' : 'upcoming'
            focusWindow = alt
          } else {
            verdict = 'upcoming'
            focusWindow = best
          }
        } else {
          verdict = 'plan-next-window'
          focusWindow = alt ?? best
        }
      }
    } catch {
      // Grass/region combo the seeding engine can't resolve: fall back to a
      // quantity-only result (no windows), same as an unknown region.
    }
  }

  // ---- Product recommendation (verified catalog, real ASINs) ----
  let product: OverseedProduct | null = null
  if (meta.seedKey) {
    const rec = getProductByKey(meta.seedKey as ProductKey)
    const seedPick = SEED_BY_GRASS[normalizeGrass(meta.display)]
    if (rec) {
      product = {
        name: rec.name,
        url: rec.affiliateUrl,
        imageUrl: rec.imageUrl ?? null,
        why: seedPick?.reason ?? rec.description,
      }
    }
  }

  const starterRec = getProductByKey(STARTER.key as ProductKey)
  const starterProduct = starterRec ? { name: starterRec.name, url: starterRec.affiliateUrl } : null

  return {
    region,
    regionKnown,
    season: meta.season,
    grassId: input.grassType,
    grassLabel: meta.label,
    grassDisplay: meta.display,
    mode: stolonsOnly ? 'stolons-only' : 'seed',
    condition,
    verdict,
    windows,
    focusWindow,
    seed,
    plugTrays,
    product,
    starterProduct,
    showWinterRyegrass: meta.season === 'warm' && !stolonsOnly && regionKnown && WINTER_RYE_REGIONS.has(region),
    showBareNudge: !stolonsOnly && condition === 'mostly-bare',
  }
}
